import React, { useEffect, useState } from 'react'
import { makeStyles, Box, Chip } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { deleteSessionById, getAllSubEvents, ALL_DATES_FILTER } from '../../../../../store/session'
import { toast } from 'utils/notify'
import { renderCol } from 'utils/renderColumns'
import {
  DeleteOutlineOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  ForumOutlined
} from '@material-ui/icons'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import ConfirmModal from 'components/ConfirmationModal'
import getTableOptions from 'utils/getTableOptions'
import moment from 'moment'

const SubEventsListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { subEvents, selectedDateFilter } = useSelector((state) => state.session)
  const { conference } = useSelector((state) => state.conference)

  const [deletedSessionId, setDeletedSessionId] = useState('')
  const [performingAction, setPerformingAction] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formattedSubEvents, setFormattedSubEvents] = useState([])
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])
  const [originalDates, setOriginalDates] = useState([]) // Store original dates without counts

  const options = getTableOptions()

  const handleDeleteSession = async () => {
    if (deletedSessionId) {
      setPerformingAction(true)
      await dispatch(deleteSessionById(deletedSessionId))
      await dispatch(getAllSubEvents(conference))
      toast.success('Sub Event deleted.')
    }
    setPerformingAction(false)
  }

  const closeModal = () => {
    setDeletedSessionId('')
  }

  useEffect(() => {
    const fetch = async () => {
      if (conference) {
        setIsLoading(true)
        await dispatch(getAllSubEvents(conference))
        setIsLoading(false)
      }
    }
    fetch()
  }, [conference])

  useEffect(() => {
    if (subEvents?.length && conference) {
      const filters = []
      const data = subEvents.reduce((prev, cur) => {
        const startDateObj =
          typeof cur.startDate === 'string'
            ? moment(cur.startDate, 'MM/DD/YYYY hh:mm A')
            : moment(cur.startDate)

        const date = startDateObj.tz(conference?.timezone).format('M/D/YYYY')

        if (!prev[date]) {
          filters.push(date)
          prev[date] = [cur]
        } else {
          prev[date].push(cur)
        }
        return prev
      }, {})
      data[ALL_DATES_FILTER] = subEvents

      // Store original dates (without counts) for accessing the data object
      const sortedDates = [ALL_DATES_FILTER, ...filters.sort()]
      setOriginalDates(sortedDates)

      // Create date filters with counts for display
      const filtersWithCount = sortedDates.map(
        (date) => `${date} (${date === ALL_DATES_FILTER ? subEvents.length : data[date].length})`
      )

      setDateFilters(filtersWithCount)
      setFormattedSubEvents(data)
    }
  }, [subEvents, conference])

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'Parent Event',
    'Sub Event',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (subEvents) =>
    subEvents.map((subEvent) => [
      subEvent?.srNo,
      subEvent?.event?.title,
      subEvent.title,
      subEvent.startDate,
      subEvent.endDate,
      <div style={{ width: 130 }}>
        {conference?.enableAcademics && (
          <ForumOutlined
            color="action"
            style={{ marginRight: 10, cursor: 'pointer' }}
            onClick={() =>
              history.push(
                `/admin/schedule/events/${subEvent.eventId}/sub-events/${subEvent.id}/talks`
              )
            }
          />
        )}
        <EditIcon
          color="action"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => {
            history.push(`${location.pathname}/sub-events/edit/${subEvent.id}`)
          }}
        />
        <DeleteIcon
          color="action"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => setDeletedSessionId(subEvent?.id)}
        />
      </div>
    ])

  return (
    <>
      <Box className={classes.filterStatsBar}>
        <div className={classes.chipList}>
          {dateFilters?.map((filter, index) => {
            // Get the corresponding original date (without count)
            const originalDate = originalDates[index]
            return (
              <Chip
                key={filter}
                label={filter}
                clickable
                color={selectedDateFilter === originalDate ? 'primary' : 'default'}
                onClick={() =>
                  dispatch({ type: 'session/setSelectedDateFilter', payload: originalDate })
                }
                className={classes.chip}
              />
            )
          })}
        </div>
      </Box>

      <Wrapper inProgress={isLoading} title="Sub Events">
        {subEvents?.length ? (
          <BasicTable
            data={formatTableData(formattedSubEvents[selectedDateFilter] || [])}
            columns={tableHead}
            options={options}
          />
        ) : (
          <ResourceNotFound message="No sub events available" />
        )}
      </Wrapper>

      <ConfirmModal
        dialogTitle="Delete Sub Event"
        dialogText="Are you sure you want to delete this sub event?"
        dialogText2="This will delete all the posters/talks if any are contained in the sub-event."
        open={Boolean(deletedSessionId)}
        closeModal={closeModal}
        handleConfirm={handleDeleteSession}
        inProgress={performingAction}
      />
    </>
  )
}

export default SubEventsListing

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    fontWeight: 600,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    fontSize: '26px'
  },
  filterStatsBar: {
    marginTop: '10px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    margin: '0px 8px 16px 8px'
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4
  },
  chip: {
    fontWeight: 500,
    fontSize: '15px',
    padding: '10px 8px'
  }
}))
